import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { StyleSheet, useWindowDimensions, View, ViewStyle } from "react-native";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  useAnimatedReaction,
  runOnJS,
} from "react-native-reanimated";
import {
  ResizableImage,
  ItemRef,
  RenderItemInfo,
  RenderItem,
} from "./ResizableImage";
import { Image, SPImageSource } from "arkh";

const DOUBLE_TAP_SCALE = 4;
const MAX_SCALE = 8;
const SPACE_BETWEEN_IMAGES = 40;

export const defaultRenderImage = ({
  item,
  setImageDimensions,
}: RenderItemInfo<SPImageSource>) => {
  return (
    <Image
      onLoad={(e) => {
        const { height: h, width: w } = e.source;
        setImageDimensions({ height: h, width: w });
      }}
      {...item}
      contentFit="contain"
      style={StyleSheet.absoluteFillObject}
    />
  );
};

type GalleryRef = {
  setIndex: (newIndex: number) => void;
  reset: (animated?: boolean) => void;
};

type GalleryReactRef = React.Ref<GalleryRef>;

type GalleryProps<T> = {
  ref?: GalleryReactRef;
  data: T[];

  renderItem?: RenderItem<T>;
  keyExtractor?: (item: T, index: number) => string | number;
  initialIndex?: number;
  onIndexChange?: (index: number) => void;
  numToRender?: number;
  emptySpaceWidth?: number;
  doubleTapScale?: number;
  doubleTapInterval?: number;
  maxScale?: number;
  style?: ViewStyle;
  containerDimensions?: { width: number; height: number };
  pinchEnabled?: boolean;
  disableTransitionOnScaledImage?: boolean;
  hideAdjacentImagesOnScaledImage?: boolean;
  loop?: boolean;
  onScaleOrTranslationChange: (params: {
    scale: number;
    translateX: number;
    translateY: number;
  }) => void;
};

const GalleryComponent = <T extends any>(
  {
    data,
    renderItem,
    initialIndex = 0,
    numToRender = 5,
    emptySpaceWidth = SPACE_BETWEEN_IMAGES,
    doubleTapScale = DOUBLE_TAP_SCALE,
    doubleTapInterval = 500,
    maxScale = MAX_SCALE,
    pinchEnabled = true,
    disableTransitionOnScaledImage = false,
    hideAdjacentImagesOnScaledImage = false,
    onIndexChange,
    style,
    keyExtractor,
    containerDimensions,
    loop = false,
    onScaleOrTranslationChange,
  }: GalleryProps<T>,
  ref: GalleryReactRef
) => {
  "use no memo";
  const windowDimensions = useWindowDimensions();
  const dimensions = containerDimensions || windowDimensions;

  const isLoop = loop && data?.length > 1;

  const [index, setIndex] = useState(initialIndex);

  const refs = useRef<ItemRef[]>([]);

  const setRef = useCallback((index: number, value: ItemRef) => {
    refs.current[index] = value;
  }, []);

  const translateY = useSharedValue(
    initialIndex * -(dimensions.height + emptySpaceWidth)
  );

  const currentIndex = useSharedValue(initialIndex);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
  }));

  const changeIndex = useCallback(
    (newIndex: number) => {
      onIndexChange?.(newIndex);
      setIndex(newIndex);
    },
    [onIndexChange, setIndex]
  );

  useAnimatedReaction(
    () => currentIndex.value,
    (newIndex) => runOnJS(changeIndex)(newIndex),
    [currentIndex, changeIndex]
  );

  useEffect(() => {
    translateY.value = index * -(dimensions.height + emptySpaceWidth);
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowDimensions]);

  useImperativeHandle(ref, () => ({
    setIndex(newIndex: number) {
      refs.current?.[index].reset(false);
      setIndex(newIndex);
      currentIndex.value = newIndex;
      translateY.value = newIndex * -(dimensions.height + emptySpaceWidth);
    },
    reset(animated = false) {
      refs.current?.forEach((itemRef) => itemRef.reset(animated));
    },
  }));

  useEffect(() => {
    if (index >= data.length) {
      const newIndex = data.length - 1;
      setIndex(newIndex);
      currentIndex.value = newIndex;
      translateY.value = newIndex * -(dimensions.height + emptySpaceWidth);
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.length]);

  return (
    <View style={[{ flex: 1, backgroundColor: "black" }, style]}>
      <Animated.View
        style={[{ flex: 1, flexDirection: "column" }, animatedStyle]}
      >
        {data.map((item: any, i) => {
          const isFirst = i === 0;

          const outOfLoopRenderRange =
            !isLoop ||
            (Math.abs(i - index) < data.length - (numToRender - 1) / 2 &&
              Math.abs(i - index) > (numToRender - 1) / 2);

          const hidden =
            Math.abs(i - index) > (numToRender - 1) / 2 && outOfLoopRenderRange;

          return (
            <View
              key={
                keyExtractor
                  ? keyExtractor(item, i)
                  : item.id || item.key || item._id || item
              }
              style={[
                dimensions,
                isFirst ? {} : { marginTop: emptySpaceWidth },
                { zIndex: index === i ? 1 : 0 },
              ]}
            >
              {hidden ? null : (
                // @ts-ignore
                <ResizableImage
                  {...{
                    translateY,
                    item,
                    currentIndex,
                    index: i,
                    isFirst,
                    isLast: i === data.length - 1,
                    length: data.length,
                    renderItem,
                    emptySpaceHeight: emptySpaceWidth,
                    doubleTapScale,
                    doubleTapInterval,
                    maxScale,
                    pinchEnabled,
                    disableTransitionOnScaledImage,
                    hideAdjacentImagesOnScaledImage,
                    loop: isLoop,
                    setRef,
                    onScaleOrTranslationChange,
                    ...dimensions,
                  }}
                />
              )}
            </View>
          );
        })}
      </Animated.View>
    </View>
  );
};

const Gallery = React.forwardRef(GalleryComponent) as <T extends any>(
  p: GalleryProps<T> & { ref?: GalleryReactRef }
) => React.ReactElement;

export default Gallery;
